// ** React Imports
import { useMemo, memo } from "react";
import { useHistory } from "react-router-dom";

// ** Redux
import { useDispatch, useSelector } from "react-redux";
import { openAuthModal } from "store/modules/global-common.slice";

// ** Third Party Components
import HoverVideoPlayer from "components/video/HoverVideoPlayer";

// ** Custom Components
import { ImageMasonry } from "components/media/components/ImageMasonry";
import { StyledMasonryCardWrapperNew } from "./styles/MasonryCardWrapperNew.styled";
import UserMediaSkeletor from "components/user/UserMediaSkeletor";
import useMobileDetect from "hooks/useMobileDetectHook";
import { StyledMediaWrapper } from "../../media/styles";
import { FakeActionButton } from "../FakeActionButton";
import DatePresenter from "components/other/DatePresenter";
import Text from "components/typography/Text";
import Block from "components/layout/Block";
import { StyledOverlay } from "styles/components/Overlay.styled";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import pseudoFormat from "utils/pseudoFormat";

// ** Third Party Components
import { useInView } from "react-intersection-observer";

// ** Custom Hooks
import { useCalculateMediaItemPadding } from "hooks/useCalculateMediaItemPadding";

// ** Routes
import containers from "containers";

// #####################################################

const MasonryVideo = ({
	withoutLazyLoading = false,
	contentId,
	contentHeight,
	contentWidth,
	contentAlbum,
	contentType = "video",
	contentPreviewVertical,
	contentSourceFull,
	contentPosterVertical,
	contentLikesCount = 0,
	contentCommentsCount = 0,
	userId,
	userLogin,
	userAvatar,
	userOnline,
	userStatusVip,
	contentData,
	isUserGallery,
	locationSearch = "",
	restOfPadding = 75, // IMPORTANT: The total height of the elements above and below the image.
}) => {
	const isAuth = useSelector((state) => state.globalCommon.isAuth);
	const dispatch = useDispatch();
	const history = useHistory();

	const to = useMemo(() => {
		const generatedPath = generatePath(containers.modal.path, {
			contentId,
			login: userLogin?.toLowerCase(),
			type: contentType,
			album: contentAlbum,
		}).toLowerCase();
		return { hash: generatedPath, search: locationSearch };
	}, [isAuth, contentId, userLogin, containers.modal.path]);

	const inlineStyledMemonized = useMemo(() => {
		return { width: "100%", position: "relative" };
	}, []);

	const mobileDetect = useMobileDetect();
	const isIos = mobileDetect.isIos();

	const { ref, inView } = useInView({
		threshold: 0,
		triggerOnce: false,
		rootMargin: "100px 0px 100px 0px",
		initialInView: true,
	});

	const padding = useCalculateMediaItemPadding({
		width: contentWidth,
		height: contentHeight,
		isSquared: false,
		withRatio_4_3: true,
	});

	const memoizedUser = useMemo(() => {
		return {
			id: userId,
			avatar: userAvatar,
			login: userLogin,
			status_vip: userStatusVip,
			online: userOnline,
		};
	}, []);

	const likesUnderMasonryRender = () => {
		if (isUserGallery) {
			return (
				<div style={inlineStyledMemonized}>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							margin: "0.5rem 0.75rem",
							justifyContent: "space-between",
							width: "calc(100% - 1.5rem)",
						}}
					>
						<Block
							mr={1}
							style={{
								marginTop: "-1px",
								position: "relative",
							}}
						>
							<Text
								color={"grey"}
								style={{ verticalAlign: "bottom" }}
								size={"0.8rem"}
							>
								<DatePresenter dateOnly date={contentData} />
							</Text>
						</Block>
						<Block flex alignCenter textNowrap>
							<FakeActionButton
								isUserGalleryProfile
								isHeart
								label={
									contentLikesCount > 0
										? pseudoFormat(contentLikesCount)
										: null
								}
								labelSize="0.8rem"
								customSvgSize="11px"
								customMargin="0 0 0 0"
								customMarginLeft="3.5px"
								customPadding="0"
							/>
						</Block>
					</div>
				</div>
			);
		}

		return (
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					margin: "0.5rem 0.75rem",
				}}
			>
				<FakeActionButton
					isHeart
					label={
						contentLikesCount > 0
							? pseudoFormat(contentLikesCount)
							: null
					}
					labelSize="0.8rem"
					customSvgSize="15px"
					customMargin="0 1rem 0 0"
					customPadding="0"
				/>
				<FakeActionButton
					isComment
					labelSize="0.8rem"
					label={
						contentCommentsCount > 0
							? pseudoFormat(contentCommentsCount)
							: null
					}
					customSvgSize="15px"
					customMargin="0 0 0 0"
					customPadding="0"
				/>
			</div>
		);
	};

	// #####################################################

	return (
		<StyledMasonryCardWrapperNew
			className="noselect"
			ref={ref}
			style={{
				paddingTop: !inView
					? `calc(${padding}% + ${restOfPadding}px)`
					: "0",
			}}
			onClick={(event) => {
				if (event.target.closest("a")) {
					return;
				}

				if (!isAuth) {
					dispatch(openAuthModal());
				} else {
					history.push(to);
				}
			}}
		>
			{inView && (
				<div style={inlineStyledMemonized}>
					{!isUserGallery && (
						<div
							style={{
								margin: "0.5rem 0.75rem",
								width: "calc(100% - 1.5rem)",
							}}
						>
							<UserMediaSkeletor
								alignCenter
								noMarginBottom
								user={memoizedUser}
								size="xxxsmall"
								vipProps={{
									height: 11,
									marginBottom: 0.125,
								}}
								gapAvatarLogin={2}
								noOnlineCircle
							/>
						</div>
					)}

					<StyledMediaWrapper>
						<div style={{ zIndex: 0 }}>
							<HoverVideoPlayer
								videoSrc={
									<>
										{contentPreviewVertical && (
											<source
												src={contentPreviewVertical}
												type="video/webm"
											/>
										)}
										{contentSourceFull && (
											<source
												src={contentSourceFull}
												type="video/mp4"
											/>
										)}
									</>
								}
								playbackStartDelay={100}
								playbackRangeEnd={isIos ? 5 : undefined}
								unloadVideoOnPaused={true}
								preload="none"
								loop
								sizingMode="overlay"
								style={{
									// ** DZ-1931 - "display:initial" caused the preview of the video to be white on Firefox on Masonry, probably read this property as "display:inline".
									// ** Without display, there were problems with the gap between the login and the video, adding "display:block" helped.
									display: "block",
									overflow: "hidden",
									width: "100%",
								}}
								videoStyle={{
									filter: `${!isAuth ? "blur(7px)" : "none"}`,
								}}
								hoverOverlay={<StyledOverlay />}
								pausedOverlay={
									<ImageMasonry
										thumbnail={contentPosterVertical}
										alt=""
										width={contentWidth}
										height={contentHeight}
										withoutLazyLoading={withoutLazyLoading}
										padding={padding}
										withPlayIcon
									/>
								}
							/>
						</div>
					</StyledMediaWrapper>

					{likesUnderMasonryRender()}
				</div>
			)}
		</StyledMasonryCardWrapperNew>
	);
};

export default memo(MasonryVideo);
